/**
 * Chat surface seam (PLAN-20260929 slice 7). The conversation keeps its
 * wiring (streaming, delivery, files, questions, voice, launches) and asks
 * this context for the pieces that draw a turn, a notice, the end-of-run line
 * and the composer. The context carries only overrides; each consumer falls
 * back to the component it renders today, so a page without a provider is
 * unchanged. The new chat's screen provides design-system versions. This
 * module is neutral, imports nothing from src/next, and reads no component at
 * load time.
 */
import { createContext, useContext, type ComponentType, type ReactNode, type RefObject } from 'react'
import type { AgentResponse, ChatInput, FileAttachmentFile, SystemMessage, UserMessage } from '@agion/ui'
import type { ExecutionLogPanelProps } from './ExecutionLogPanel'
import type { ActiveHITLRequest, StreamingChatMessage } from '../../hooks/useStreamingMissionChat'
import type { HITLDecision } from '../../types/streaming'
import type { LaunchInputsForm as LaunchInputsFormState } from '../../hooks/useLaunchInputs'
import type { TrustTier } from '@agion/ui'
import type { SearchResult } from '../../services/search-api'
import type { QuestionReply, ToolOutputFile } from '@agion/mission-stream'
import type { AnswerOrigin, MessageDelivery } from '../../hooks/useAnswerDelivery'
import type { ExecutionListScope } from '../../services/execution-api'

export type UserMessageProps = React.ComponentProps<typeof UserMessage>
export type AgentResponseProps = React.ComponentProps<typeof AgentResponse>
export type SystemMessageProps = React.ComponentProps<typeof SystemMessage>
export type ChatComposerProps = React.ComponentProps<typeof ChatInput>

export type RunStatusKind = 'completed' | 'stopped' | 'failed'

export interface RunStatusLineProps {
  kind: RunStatusKind
  children: ReactNode
  'data-testid'?: string
}

export interface HistoryFailureProps {
  /** The failed read (the run's status or its messages), with its typed fields. */
  failure: unknown
  onRetry: () => void
}

/** Where the Activity panel sits: beside the conversation, or as a sheet over it (a phone). */
export type ActivityPresentation = 'aside' | 'sheet'

/**
 * The run's state as the conversation knows it (agion/Agion#2827): from the
 * run's own events and its recorded status, so a paused or failed run never
 * reads as complete. A stopped run and one that ended with an answer to check
 * are completed. Undefined before a run starts.
 */
export type ActivityRunStatus = 'running' | 'paused' | 'completed' | 'failed' | 'stopped'

/** What the Activity sidebar is given: the conversation's run data and the way to close it. */
export interface ActivitySidebarProps extends ExecutionLogPanelProps {
  onClose: () => void
  /** The mission's execution mode, for a sidebar that tints by strategy. */
  executionMode?: string
  /** The mission the conversation runs, for a sidebar that reads the run's billed cost. */
  missionId?: string
  /** The run's state, for a sidebar that states it. */
  runStatus?: ActivityRunStatus
  /**
   * Given only by a surface with useActivityPresentation (PLAN-20261001
   * slice 14); without it the sidebar sits beside the conversation.
   */
  presentation?: ActivityPresentation
}

export interface ChatSurface {
  UserMessage?: ComponentType<UserMessageProps>
  AgentResponse?: ComponentType<AgentResponseProps>
  SystemMessage?: ComponentType<SystemMessageProps>
  /** Replaces the composer; receives the current ChatInput props. */
  Composer?: ComponentType<ChatComposerProps>
  /** Replaces the completed, stopped and failed lines at the end of a run. */
  RunStatusLine?: ComponentType<RunStatusLineProps>
  /**
   * Says a past run's conversation could not be read (ADR-20260925). Without
   * it the current chat's own notice shows (agion/Agion#2481).
   */
  HistoryFailure?: ComponentType<HistoryFailureProps>
  /** Replaces the Activity sidebar beside the conversation, container included (agion/Agion#2488). */
  ActivitySidebar?: ComponentType<ActivitySidebarProps>
  /**
   * Opt-in hook (PLAN-20261001 slice 14): measures the conversation's own box
   * and says where the surface's ActivitySidebar presents. While it says
   * "sheet" the panel does not open on its own when a run starts (a sheet
   * would cover the agent's questions); the header's Open activity opens it.
   * Without it nothing is measured and the panel opens beside the
   * conversation as before. The surface owns the measurement, so this module
   * imports no phone signal of its own.
   */
  useActivityPresentation?: (containerRef: RefObject<HTMLElement | null>) => ActivityPresentation
  /**
   * Replaces an in-thread question with a fixed set of single-select answers
   * (PLAN-20260929 slice 12). The chat keeps the countdown, the governance
   * read, expiry and the handler that answers it — this only draws them.
   * Without it the current chat's own HITLPrompt shows.
   */
  Question?: ComponentType<QuestionProps>
  /** Replaces the file chips on a user turn or an answer that carried attachments. */
  FileCard?: ComponentType<FileCardProps>
  /** Replaces the downloadable file card for a tool result's output files. */
  OutputFile?: ComponentType<OutputFileProps>
  /**
   * Replaces the box around a tool result's output files; each file inside
   * is still drawn by OutputFile. Without it the current chat's indented box
   * shows.
   */
  OutputFileGroup?: ComponentType<OutputFileGroupProps>
  /** Replaces the card for a child mission a run started, its box included. */
  MissionTrigger?: ComponentType<MissionTriggerProps>
  /** Replaces the delivery line under a message this chat sent. */
  DeliveryStatus?: ComponentType<DeliveryStatusProps>
  /**
   * Opt-in pair (agion/Agion#2827): the run's steps in the conversation, as
   * the Mission Designer's run chat shows them. useRunSteps reads the steps
   * from this run's messages and RunStep draws one; the conversation places
   * each step among its messages by the time the step started. Both must be
   * given; without them the conversation shows no steps, as today.
   */
  useRunSteps?: (input: RunStepsInput) => ConversationRunStep[]
  RunStep?: ComponentType<RunStepProps>
  /** Replaces a governance escalation card (an approval with a policy rule behind it). */
  Escalation?: ComponentType<EscalationProps>
  /**
   * Replaces the in-thread question card (agion/Agion#3446): a question
   * escalation the card reads from governance and, when governance says the
   * viewer may decide it, answers through the escalation store. Without it
   * the current chat's own QuestionCard shows.
   */
  QuestionCard?: ComponentType<QuestionCardProps>
  /**
   * Opt-in (persona walkthrough F-CHAT-07): a message the run told the member
   * (a question row with modality "message", such as a step's outputs) carries
   * the same Helpful / Not helpful control as an agent's answer, sent through
   * the conversation's own feedback handler. A run read back from its stored
   * record has lost the modality, so there a message no member's words follow
   * in a completed run counts as told, without the "Answered" line. Without it
   * a told message shows no feedback control, as today.
   */
  toldMessageFeedback?: boolean
  /**
   * Opt-in (persona walkthrough F-CHAT-05): the text a told message shows,
   * from its prompt and request id. Without it the prompt shows as sent.
   */
  toldMessageText?: (prompt: string, requestId: string | undefined) => string
  /**
   * Opt-in (persona walkthrough F-CHAT-08): the mission read asks the server
   * for run_readiness, and a mission the server says cannot start shows Run
   * Mission off with the server's reason in reader words, the composer of an
   * ended run says the same, and autostart does not launch. Without it no
   * readiness is asked for or shown, as today.
   */
  runReadiness?: boolean
  /**
   * Opt-in (persona finding F-ACC-03, owner decision 2026-10-11): the server
   * scope of the run list the mission header counts and lists ("N runs" and
   * the other runs). The new chat asks for the reader's own runs and runs of
   * missions they are a member of; the server enforces it. Without it the
   * list is read as today.
   */
  runHistoryScope?: ExecutionListScope
  /** Replaces the markdown rendering of an agent's answer or a question's prompt. */
  Markdown?: ComponentType<MarkdownProps>
  /** Replaces the mission header above the conversation (name, run time, trust, reconnect state). */
  Header?: ComponentType<HeaderProps>
  /** Replaces the floating bar above the composer naming the open question being answered. */
  ContextBar?: ComponentType<ContextBarProps>
  /** Replaces the start-values form a new run asks for. */
  LaunchInputs?: ComponentType<LaunchInputsProps>
  /** Replaces the message-search results dropdown. */
  SearchResults?: ComponentType<SearchResultsProps>
  /** Replaces the compact strip shown above the composer during a voice call. */
  VoiceControls?: ComponentType<VoiceControlsProps>
}

export interface MarkdownProps {
  content: string
}

export interface HeaderProps {
  missionName: string
  executionCreatedAt: string | null
  trustTier?: TrustTier | null
  hostName?: string
  isConnected: boolean
  isHistoryOnly: boolean
  executionId: string | null
  pastExecutions?: Array<{ id: string; timestamp: Date; isActive?: boolean }>
  onNavigateExecution: (execId: string) => void
  /**
   * Message search (agion/Agion issue 2612 item 2), given only when the host
   * wants the header to carry it as an icon that expands in place of the
   * heading. Without it a Header falls back to showing no search affordance
   * of its own — the current chat keeps its separate full-width search row
   * either way, so legacy is unaffected by this prop's presence or absence.
   */
  search?: {
    query: string
    onQueryChange: (query: string) => void
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    /** The results dropdown, positioned by the host; rendered only while open. */
    resultsSlot?: ReactNode
  }
  /**
   * The header's own row actions (agion/Agion issue 2612 item 5) — e.g. Edit
   * in Designer, open the Activity sidebar, pause/stop the run. On a Header
   * that draws them inline (the next chat surface) they sit after the
   * chevron, and collapse into one overflow control below the `sm` width
   * instead of wrapping. Without it a Header shows no actions of its own —
   * the current chat keeps rendering `headerActions` in its own separate row
   * regardless, so this prop's presence changes nothing for legacy.
   */
  actions?: HeaderActionItem[]
}

export interface HeaderActionItem {
  key: string
  label: string
  icon: ComponentType<{ className?: string }>
  href?: string
  onClick?: () => void
}

export interface ContextBarProps {
  pendingHITLs: ActiveHITLRequest[]
  selectedIndex: number
  onSelectIndex: (index: number) => void
  onScrollToMessage: (requestId: string) => void
  resolveAgentName: (agentId: string) => string
}

export interface LaunchInputsProps {
  form: LaunchInputsFormState
  disabled?: boolean
  className?: string
  /**
   * The form sits above the composer of a run that has ended, where the next
   * message starts a new run with these values (persona walkthrough
   * F-CHAT-04). A surface may fold the form away and say so; the current chat
   * draws the same form either way.
   */
  afterRun?: boolean
}

export interface SearchResultsProps {
  results: SearchResult[]
  total: number
  isSearching: boolean
  error: string | null
  query: string
  hostsQueried: number
  hostsResponded: number
  onClose: () => void
}

export interface VoiceControlsProps {
  state: 'connecting' | 'in_call'
  isRemoteSpeaking: boolean
  isLocalSpeaking: boolean
  /** Seconds since the call connected. */
  durationSeconds: number
  isMuted: boolean
  onToggleMute: () => void
  onDisconnect: () => void
}

export interface QuestionProps {
  request: ActiveHITLRequest
  /**
   * decision: only from an approval card decided inline (agion/Agion#2618).
   * answer: only from a structured question's card (agion/Agion#3559).
   */
  onRespond: (requestId: string, response: string, decision?: HITLDecision, answer?: QuestionReply) => void
  /**
   * False when the question is not open: answered, its answer on its way, or
   * its run ended. Its choices then cannot send and say why.
   */
  isOpen?: boolean
}

export interface FileCardProps {
  files: FileAttachmentFile[]
}

export interface OutputFileProps {
  file: ToolOutputFile
  hostId?: string
}

export interface OutputFileGroupProps {
  /** The tool result's file cards. */
  children: ReactNode
}

export interface MissionTriggerProps {
  childMissionId: string
  childMissionName?: string
  childExecutionId: string
  /** Opens the child mission's conversation. */
  onOpenMission: (missionId: string) => void
}

export interface DeliveryStatusProps {
  delivery: MessageDelivery
  /** Where a returned send went: the message box, or the question's choices. */
  origin?: AnswerOrigin
}

export type ToolActivityStatus = 'started' | 'completed' | 'failed' | 'blocked'

export interface ToolActivityProps {
  agentName: string
  status: ToolActivityStatus
  /** What the agent was asked to do, on "started". */
  activity?: string
  /** The outcome text, on "completed" / "failed" / "blocked". */
  result?: string
  /** Seconds, on "completed" / "failed" / "blocked". */
  duration?: number
  /** Label of the blocked status, translated by the host. */
  blockedLabel?: string
}

export interface ThinkingProps {
  agentName: string
  /** What the agent is doing right now ("Thinking…"), translated by the host. */
  currentStep?: string
}

/** What the conversation knows that a run step reads. */
export interface RunStepsInput {
  /** This run's messages, live or rebuilt from its history. */
  messages: StreamingChatMessage[]
  /** The run is executing now (not paused, not ended): a running step is live. */
  live: boolean
  /** Map from agentId/nodeId to display name. */
  agentNameMap?: Record<string, string>
  orchestratorAgentId?: string
}

/** A run step's state: waiting is a step that started and is not running now (paused, or never finished). */
export type RunStepState = 'running' | 'waiting' | 'completed' | 'warning' | 'failed'

export interface RunStepProps {
  label: string
  state: RunStepState
  /** The step's length once it ended. */
  durationMs?: number
  /** A live step's newest tool calls, oldest first; empty otherwise. */
  tools: Array<{ key: string; name: string; status: 'running' | 'done' | 'failed' }>
  /** A live step's running calls the window leaves out. */
  moreTools: number
  /** The mission's execution mode, for a step that tints by strategy. */
  executionMode?: string
}

/** One step of the run, placed in the conversation at the time it started. */
export interface ConversationRunStep extends Omit<RunStepProps, 'executionMode'> {
  key: string
  startedAt: number
}

export type EscalationSeverity = 'critical' | 'high' | 'medium' | 'low'

export interface EscalationProps {
  status: 'pending' | 'expired'
  agentName: string
  /** What the agent wants to do. */
  action: string
  ruleName: string
  severity: EscalationSeverity
  expiresAt?: Date
  onApprove: () => void
  onDeny: () => void
  /** The escalation is a past run's record, not this run's open request. */
  isReadOnly?: boolean
  /**
   * Set when the card cannot be decided here (its runner does not decide
   * inline, agion/Agion#2618): the card links to Escalations at this address
   * instead of offering Approve and Deny.
   */
  decideInEscalationsHref?: string
}

export interface QuestionCardProps {
  /** The question escalation the frame names; without one the card can read nothing. */
  escalationId?: string
}

/** No overrides: the current chat. */
export const currentChatSurface: ChatSurface = {}

export const ChatSurfaceContext = createContext<ChatSurface>(currentChatSurface)

export function useChatSurface(): ChatSurface {
  return useContext(ChatSurfaceContext)
}
